import { useQuery } from '@tanstack/react-query';
import axios from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001/api/v1';

const apiClient = axios.create({
  baseURL: `${API_BASE_URL}/ai-crm`,
  headers: { 'Content-Type': 'application/json' },
});

apiClient.interceptors.request.use((cfg) => {
  const token = localStorage.getItem('authToken');
  if (token) cfg.headers.Authorization = `Bearer ${token}`;
  return cfg;
});

export interface CurrentPlan {
  id: string;
  plan: string;
  planName?: string;
  billingCycle: 'MONTHLY' | 'YEARLY' | string;
  status: string;
  crmEnabled: boolean;
  superAdminEnabled: boolean;
  maxUsers: number | null;
  maxLeads: number | null;
  currentUsers: number;
  currentLeads: number;
  currentPeriodEnd: string;
  monthlyLeadsAllocated?: Record<string, number> | null;
  amount?: number;
  currency?: string;
  trialEndsAt?: string | null;
  /** Convenience: true when plan === 'TRIAL' (promo-redeemed signup). */
  isTrial: boolean;
  /** Convenience: true when trial has expired (lockout state). */
  isTrialExpired: boolean;
}

interface ApiSubscription {
  id: string;
  plan: string;
  billingCycle: string;
  status: string;
  crmEnabled?: boolean;
  superAdminEnabled?: boolean;
  maxUsers?: number | null;
  maxLeads?: number | null;
  currentUsers?: number;
  currentLeads?: number;
  currentPeriodEnd: string;
  monthlyLeadsAllocated?: Record<string, number> | null;
  subscriptionPlan?: { name?: string; displayName?: string } | null;
  amount?: string | number;
  currency?: string;
  trialEndsAt?: string | null;
}

interface ApiUsage {
  users?: { current: number; limit: number | null };
  leads?: { current: number; limit: number | null };
}

interface ApiCurrentPlanResponse {
  subscription: ApiSubscription;
  usage?: ApiUsage;
}

export const useCurrentPlan = () => {
  return useQuery<CurrentPlan>({
    queryKey: ['current-plan'],
    queryFn: async () => {
      const res = await apiClient.get<{ success: boolean; data: ApiCurrentPlanResponse }>(
        '/subscriptions/current',
      );
      // Backend returns { subscription, usage } nested — not the subscription
      // at the top level. Pull both.
      const s = res.data.data.subscription;
      const u = res.data.data.usage;
      return {
        id: s.id,
        plan: s.plan,
        planName: s.subscriptionPlan?.displayName ?? s.subscriptionPlan?.name ?? s.plan,
        billingCycle: s.billingCycle,
        status: s.status,
        crmEnabled: s.crmEnabled ?? false,
        superAdminEnabled: s.superAdminEnabled ?? false,
        maxUsers: s.maxUsers ?? null,
        maxLeads: s.maxLeads ?? null,
        // Prefer live usage counts from the server's lead/user aggregations;
        // fall back to the columns on the subscription row if absent.
        currentUsers: u?.users?.current ?? s.currentUsers ?? 0,
        currentLeads: u?.leads?.current ?? s.currentLeads ?? 0,
        currentPeriodEnd: s.currentPeriodEnd,
        monthlyLeadsAllocated: s.monthlyLeadsAllocated ?? null,
        amount: s.amount !== undefined && s.amount !== null ? Number(s.amount) : undefined,
        currency: s.currency,
        trialEndsAt: s.trialEndsAt ?? null,
        isTrial: s.plan === 'TRIAL',
        isTrialExpired:
          s.plan === 'TRIAL' &&
          s.trialEndsAt != null &&
          new Date(s.trialEndsAt).getTime() < Date.now(),
      };
    },
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
};
